import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, Phone, Mail, MapPin, Paperclip, Download, Trash2, Upload, ChevronDown, ChevronUp, Stethoscope, FileText, Copy, Check, PackageIcon, Droplet, Briefcase, UserPlus, PanelLeftOpen, Eye, EyeOff, Loader2, Pencil } from "lucide-react";
import ConsultationHistorySection from "@/components/crm/ConsultationHistory";
import { ChartingTab, BillingTab, MedicalHistoryPanel } from "@/components/crm/PatientTimeline";
import PatientQueueRail from "@/components/crm/PatientQueueRail";
import EditPatientDialog, { canEditPatient } from "@/components/crm/EditPatientDialog";
import { PATIENT_EDIT } from "@/constants/testIds";

// The patient ID exists to be read down a phone and quoted at the desk, so make
// it copyable rather than something staff retype from the screen.
function PatientNo({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
      .catch(() => toast.error("Could not copy"));
  };
  return (
    <button
      onClick={copy}
      title="Copy patient ID"
      className="mt-0.5 flex items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-navy"
      data-testid="patient-no"
    >
      {value}
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

// Expiry state for a package, or null when it never expires. Amber inside 30
// days is the window where the front desk can still do something about it.
function expiryChip(expiresAt) {
  if (!expiresAt) return null;
  const days = Math.ceil((new Date(`${expiresAt.slice(0, 10)}T00:00:00`) - new Date()) / 86400000);
  if (days < 0) return { label: "Expired", tone: "bg-red-100 text-red-700" };
  if (days <= 30) return { label: `Expires in ${days}d`, tone: "bg-amber-100 text-amber-800" };
  return { label: `Valid to ${formatDate(expiresAt)}`, tone: "bg-navy/5 text-muted-foreground" };
}

const TREATMENT_STATUS = {
  started: { label: "Started", color: "bg-blue-100 text-blue-800" },
  follow_up: { label: "Follow-up", color: "bg-amber-100 text-amber-800" },
  completed: { label: "Completed", color: "bg-green-100 text-green-800" },
};

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // treatment being managed
  const [logSession, setLogSession] = useState(null); // treatment to log a session for
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [railKey, setRailKey] = useState(0); // bump → the queue rail refetches (new name)
  // Charting is the default: the visit timeline is how the clinic thinks about
  // a patient. Packages keep their own tab — still our differentiator, just no
  // longer the front door.
  const [tab, setTab] = useState("charting");
  // The day's queue beside the chart. Remembered per browser: a receptionist
  // wants it open all day, a doctor on a laptop wants the width back.
  const [queueOpen, setQueueOpen] = useState(
    () => localStorage.getItem("patientQueueOpen") !== "0");
  const toggleQueue = (open) => {
    setQueueOpen(open);
    localStorage.setItem("patientQueueOpen", open ? "1" : "0");
  };

  // Numbers are masked; "View" reveals them via the audited
  // POST /patients/:id/phone-view (counted in Reports → Number Views).
  // Hide drops them, so every View is a fresh logged call — same as LeadDetail.
  const [phoneReveal, setPhoneReveal] = useState(null);
  const [phoneRevealing, setPhoneRevealing] = useState(false);
  useEffect(() => { setPhoneReveal(null); }, [id]);
  const togglePhoneReveal = async () => {
    if (phoneReveal) { setPhoneReveal(null); return; }
    setPhoneRevealing(true);
    try {
      const { data: d } = await api.post(`/patients/${id}/phone-view`, { source: "web" });
      setPhoneReveal(d || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPhoneRevealing(false); }
  };

  const load = useCallback(() => {
    api.get(`/patients/${id}`).then((r) => setData(r.data)).catch(() => navigate("/patients"));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users", { params: { role: "doctor" } }).then((r) => setDoctors(r.data)).catch(() => {});
  }, []);

  const docName = (did) => doctors.find((d) => d.id === did)?.name || "—";

  const updateTreatment = async (tid, body) => {
    try {
      await api.put(`/patients/${id}/treatments/${tid}`, body);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Generic upload — can attach to the patient generally, or to a session.
  const uploadDocument = async (file, extra = {}) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", extra.doc_type || "other");
    formData.append("patient_id", id);
    if (extra.treatment_id) formData.append("treatment_id", extra.treatment_id);
    if (extra.session_id) formData.append("session_id", extra.session_id);
    try {
      await api.post("/documents", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteDocument = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteSession = async (tid, sid) => {
    try {
      await api.delete(`/treatments/${tid}/sessions/${sid}`);
      toast.success("Session deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadDocument = async (doc) => {
    const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", doc.name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  if (!data) return <div className="py-12 text-center text-muted-foreground">Loading…</div>;
  const { patient, treatments, documents = [] } = data;
  const money = patient.money || { collected: 0, billed: 0, outstanding: 0 };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/patients")} className="flex items-center gap-2 text-sm font-medium text-navy hover:underline" data-testid="back-to-patients">
          <ArrowLeft className="h-4 w-4" /> Back to patients
        </button>
        {!queueOpen && (
          <button onClick={() => toggleQueue(true)} data-testid="show-queue-btn"
            className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy">
            <PanelLeftOpen className="h-4 w-4" /> Show the day's queue
          </button>
        )}
      </div>

      {/* With the queue open the patient card needs a floor, not a fraction:
          at 1fr it collapsed to ~340px and wrapped the patient's name onto two
          lines. 300px is the width the card was designed against. */}
      <div className={`grid gap-6 ${queueOpen
        ? "xl:grid-cols-[210px_minmax(300px,0.85fr)_minmax(0,2fr)]"
        : "lg:grid-cols-3"}`}>
        {queueOpen && (
          <PatientQueueRail currentId={id} onClose={() => toggleQueue(false)} refreshKey={railKey} />
        )}
        <div className="space-y-6">
        <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-navy/10 font-display text-2xl font-semibold text-navy">{patient.name?.charAt(0)}</div>
            <div className="min-w-0">
              <h2 className="font-display text-2xl font-semibold text-navy">{patient.name}</h2>
              <p className="text-sm text-muted-foreground">{patient.gender}{patient.age ? `, ${patient.age} yrs` : ""}</p>
              {patient.patient_no && <PatientNo value={patient.patient_no} />}
              {/* What they owe, next to their name. It was only visible further
                  down inside the Lifetime Collected card — reception needs it
                  the moment the chart opens, not after a scroll. */}
              {money.outstanding > 0 && (
                <p className="mt-1 text-sm font-semibold text-red-600" data-testid="patient-balance-due">
                  {formatCurrency(money.outstanding)} due
                </p>
              )}
            </div>
            {canEditPatient(user) && (
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} title="Edit details"
                className="ml-auto shrink-0 self-start" data-testid={PATIENT_EDIT.openButton}>
                <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
              </Button>
            )}
          </div>
          <div className="mt-5 space-y-2.5 text-sm">
            <p className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-3.5 w-3.5" />
              <span data-testid="patient-detail-mobile">{phoneReveal ? (phoneReveal.mobile ?? patient.mobile) : maskPhone(patient.mobile)}</span>
              {patient.mobile && (
                <button
                  type="button"
                  onClick={togglePhoneReveal}
                  disabled={phoneRevealing}
                  className="inline-flex items-center gap-1 rounded-md border border-navy/15 px-1.5 py-0.5 text-xs font-medium text-navy hover:bg-navy/5 disabled:opacity-50"
                  data-testid="patient-phone-view-btn"
                >
                  {phoneRevealing ? <Loader2 className="h-3 w-3 animate-spin" /> : phoneReveal ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  {phoneRevealing ? "Loading…" : phoneReveal ? "Hide" : "View"}
                </button>
              )}
            </p>
            {phoneReveal && [].concat(phoneReveal.alt_mobile || []).filter(Boolean).map((n) => (
              <p key={n} className="flex items-center gap-2 text-muted-foreground"><Phone className="h-3.5 w-3.5" /> {n} <span className="text-xs">(alt)</span></p>
            ))}
            {patient.email && <p className="flex items-center gap-2 text-muted-foreground"><Mail className="h-3.5 w-3.5" /> {patient.email}</p>}
            {(patient.address || patient.city) && (
              <p className="flex items-start gap-2 text-muted-foreground">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{[patient.address, patient.city, patient.pincode].filter(Boolean).join(", ")}</span>
              </p>
            )}
            {patient.blood_group && (
              <p className="flex items-center gap-2 text-muted-foreground">
                <Droplet className="h-3.5 w-3.5" /> {patient.blood_group}
              </p>
            )}
            {patient.occupation && (
              <p className="flex items-center gap-2 text-muted-foreground">
                <Briefcase className="h-3.5 w-3.5" /> {patient.occupation}
              </p>
            )}
            {patient.referred_by && (
              <p className="flex items-center gap-2 text-muted-foreground">
                <UserPlus className="h-3.5 w-3.5" /> Referred by {patient.referred_by}
              </p>
            )}
            {patient.groups?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {patient.groups.map((g) => (
                  <span key={g} className="rounded-full bg-gold/15 px-2 py-0.5 text-xs font-medium text-navy">{g}</span>
                ))}
              </div>
            )}
          </div>
          {/* Money that actually moved, from invoice payments. This used to read
              patient.total_revenue, which sums TREATMENT amounts — so a patient
              billed for a plain consultation showed ₹0 after paying. */}
          <div className="mt-5 rounded-lg bg-nude/60 p-4 text-center">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Lifetime Collected</p>
            <p className="font-display text-3xl font-semibold text-gold">{formatCurrency(money.collected)}</p>
            <div className="mt-2 flex items-center justify-center gap-3 border-t border-navy/10 pt-2 text-xs">
              <span className="text-muted-foreground">Billed {formatCurrency(money.billed)}</span>
              {money.outstanding > 0 && (
                <span className="font-medium text-red-600">{formatCurrency(money.outstanding)} due</span>
              )}
            </div>
          </div>
        </div>

        {/* Allergies and comorbidities, permanently on screen. Anyone about to
            inject a filler or prescribe isotretinoin needs this without
            hunting for it. */}
        <MedicalHistoryPanel patient={patient} onSaved={load} />
        </div>

        {/* col-span-2 is for the 3-equal-column layout with the queue hidden.
            With the queue open the template already gives this column its own
            track, and spanning two pushed it onto a second row — leaving the
            chart invisible below the fold. */}
        <div className={`space-y-6 ${queueOpen ? "" : "lg:col-span-2"}`}>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="charting" data-testid="tab-charting">Charting</TabsTrigger>
              <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
              <TabsTrigger value="packages" data-testid="tab-packages">Packages</TabsTrigger>
            </TabsList>
          </Tabs>

          {tab === "charting" && <ChartingTab patientId={id} patient={patient} onChanged={load} />}
          {tab === "billing" && <BillingTab patientId={id} onChanged={load} />}

          {tab === "packages" && (
          <>
          <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold text-navy">Treatments</h3>
              <Button size="sm" onClick={() => setOpen(true)} className="bg-navy text-white hover:bg-navy-hover" data-testid="add-treatment-btn"><Plus className="mr-1.5 h-4 w-4" /> Add</Button>
            </div>
            <div className="mt-4 space-y-3">
              {treatments.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No treatments recorded</p> :
                treatments.map((t) => (
                  <TreatmentCard
                    key={t.id}
                    treatment={t}
                    docName={docName}
                    onManage={() => setEditing(t)}
                    onLogSession={() => setLogSession(t)}
                    onUploadToSession={(file, sid) => uploadDocument(file, { treatment_id: t.id, session_id: sid, doc_type: "session" })}
                    onDeleteSession={(sid) => deleteSession(t.id, sid)}
                    onDownload={downloadDocument}
                    onDeleteDoc={deleteDocument}
                  />
                ))}
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
            <h3 className="font-display text-lg font-semibold text-navy">Documents</h3>
            <label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-navy/20 bg-cream/40 py-6 text-sm font-medium text-navy hover:bg-cream/60" data-testid="upload-document-label">
              <Upload className="h-4 w-4" /> Upload prescription, ID, or photo
              <input
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                onChange={(e) => { uploadDocument(e.target.files[0]); e.target.value = ""; }}
                data-testid="upload-document-input"
              />
            </label>
            <div className="mt-4 space-y-2">
              {documents.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No documents uploaded</p> :
                documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between rounded-lg bg-cream/60 p-3" data-testid="document-item">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">{d.name}</p>
                        <p className="text-xs text-muted-foreground">{d.uploaded_by_name} · {formatDateTime(d.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button size="icon" variant="ghost" onClick={() => downloadDocument(d)} data-testid="download-document-btn"><Download className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteDocument(d.id)} data-testid="delete-document-btn"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Package ledger — sessions remaining and money outstanding */}
          <PackageLedger patientId={id} onChanged={load} />

          <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-navy" />
                <h3 className="font-display text-lg font-semibold text-navy">Invoices</h3>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate(`/invoices?patient_id=${id}`)} className="border-navy/20 text-navy">
                View All
              </Button>
            </div>
            <PatientInvoices patientId={id} />
          </div>
          </>
          )}
        </div>
      </div>

      {/* Vitals trend across every consultation. Kept out of the timeline
          because it is the one view that only makes sense ACROSS visits. */}
      {tab === "charting" && (
        <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-navy" />
            <h3 className="font-display text-lg font-semibold text-navy">Vitals trend</h3>
          </div>
          <ConsultationHistorySection patientId={id} />
        </div>
      )}

      <AddTreatment open={open} onOpenChange={setOpen} patientId={id} onDone={load} />
      <ManageTreatment treatment={editing} onClose={() => setEditing(null)} onSave={updateTreatment} />
      <LogSessionDialog treatment={logSession} doctors={doctors} onClose={() => setLogSession(null)} onDone={load} />
      {canEditPatient(user) && (
        <EditPatientDialog patient={patient} open={editOpen} onClose={() => setEditOpen(false)}
          onSaved={() => { setPhoneReveal(null); load(); setRailKey((k) => k + 1); }} />
      )}
    </div>
  );
}

function TreatmentCard({ treatment: t, docName, onManage, onLogSession, onUploadToSession, onDeleteSession, onDownload, onDeleteDoc }) {
  const [expanded, setExpanded] = useState(false);
  const pct = t.sessions_total ? Math.round((t.sessions_done / t.sessions_total) * 100) : 0;
  const st = TREATMENT_STATUS[t.status] || TREATMENT_STATUS.started;
  const sessions = t.sessions || [];
  const label = t.package_name || t.package;
  const money = t.ledger;
  const paidPct = money?.agreed_price ? Math.min(100, Math.round((money.amount_paid / money.agreed_price) * 100)) : 0;
  const expiry = expiryChip(t.expires_at);

  return (
    <div className="rounded-lg border border-navy/10 p-4" data-testid={`treatment-${t.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-ink">{t.name}</p>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.color}`}>{st.label}</span>
            {t.package_id && <span className="rounded bg-navy/10 px-1.5 py-0.5 text-[10px] font-semibold text-navy">PKG</span>}
            {expiry && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${expiry.tone}`}>{expiry.label}</span>
            )}
          </div>
          {/* package_name (catalogue snapshot) || package (legacy free text) —
              the back-compat rule that keeps old rows rendering unchanged. */}
          <p className="text-xs text-muted-foreground">{label ? `${label} · ` : ""}{formatDate(t.created_at)}</p>
        </div>
        <span className="font-display font-semibold text-gold">{formatCurrency(t.amount)}</span>
      </div>
      <div className="mt-3">
        <div className="mb-1 flex justify-between text-xs text-muted-foreground"><span>Sessions {t.sessions_done}/{t.sessions_total}</span><span>{pct}%</span></div>
        <Progress value={pct} className="h-2" />
      </div>
      {/* Money line, from the scalars already inlined on GET /patients/{id} —
          no per-card fetch. Only rendered where the figures mean something. */}
      {money && (
        <div className="mt-2">
          <div className="mb-1 flex justify-between text-xs">
            <span className="text-muted-foreground">
              Paid <span className="font-medium text-ink">{formatCurrency(money.amount_paid)}</span> of {formatCurrency(money.agreed_price)}
            </span>
            {money.balance_due > 0
              ? <span className="font-medium text-red-600">{formatCurrency(money.balance_due)} due</span>
              : <span className="font-medium text-green-700">Paid in full</span>}
          </div>
          <Progress value={paidPct} className="h-1.5" />
          {money.unbilled > 0 && (
            <p className="mt-1 text-[11px] text-amber-700">{formatCurrency(money.unbilled)} not yet invoiced</p>
          )}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onLogSession} className="h-8 bg-navy text-white hover:bg-navy-hover" data-testid={`treatment-log-session-${t.id}`}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Log Session
        </Button>
        <Button size="sm" variant="outline" onClick={() => setExpanded((v) => !v)} className="h-8 border-navy/20 text-navy hover:bg-navy/5" data-testid={`treatment-toggle-${t.id}`}>
          {expanded ? <ChevronUp className="mr-1 h-3.5 w-3.5" /> : <ChevronDown className="mr-1 h-3.5 w-3.5" />}
          {sessions.length} session{sessions.length !== 1 ? "s" : ""}
        </Button>
        {/* Points at the Consultation History section below, so the two stop
            reading as unrelated lists. */}
        {t.consultation_count > 0 && (
          <span className="text-xs text-muted-foreground" data-testid={`treatment-consults-${t.id}`}>
            · {t.consultation_count} consultation{t.consultation_count !== 1 ? "s" : ""}
          </span>
        )}
        <Button size="sm" variant="ghost" onClick={onManage} className="h-8 text-muted-foreground hover:text-ink" data-testid={`treatment-manage-${t.id}`}>
          Manage
        </Button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3 border-t border-navy/10 pt-4">
          {sessions.length === 0 ? (
            <p className="py-2 text-center text-xs text-muted-foreground">No sessions logged yet. Use “Log Session” to record a visit.</p>
          ) : sessions.map((s, i) => (
            <SessionRow
              key={s.id}
              session={s}
              index={sessions.length - i}
              docName={docName}
              onUpload={(file) => onUploadToSession(file, s.id)}
              onDelete={() => onDeleteSession(s.id)}
              onDownload={onDownload}
              onDeleteDoc={onDeleteDoc}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SessionRow({ session: s, index, docName, onUpload, onDelete, onDownload, onDeleteDoc }) {
  const docs = s.documents || [];
  return (
    <div className="rounded-lg bg-cream/50 p-3" data-testid={`session-${s.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-semibold text-ink">Visit #{index} · {formatDate(s.session_date)}</p>
          <p className="text-xs text-muted-foreground">Dr. {docName(s.doctor_id)}{s.created_by_name ? ` · logged by ${s.created_by_name}` : ""}</p>
        </div>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onDelete} data-testid={`session-delete-${s.id}`}>
          <Trash2 className="h-3.5 w-3.5 text-red-500" />
        </Button>
      </div>
      <div className="mt-2 space-y-1.5 text-sm">
        {s.notes && <p><span className="font-medium text-navy">Notes:</span> <span className="text-muted-foreground">{s.notes}</span></p>}
        {s.prescription && <p><span className="font-medium text-navy">Prescription:</span> <span className="text-muted-foreground">{s.prescription}</span></p>}
        {s.observations && <p><span className="font-medium text-navy">Observations:</span> <span className="text-muted-foreground">{s.observations}</span></p>}
        {s.next_visit && <p><span className="font-medium text-navy">Next visit:</span> <span className="text-muted-foreground">{formatDate(s.next_visit)}</span></p>}
      </div>
      {/* Files attached to this session */}
      <div className="mt-2 space-y-1.5">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-md bg-white px-2.5 py-1.5" data-testid={`session-doc-${d.id}`}>
            <div className="flex min-w-0 items-center gap-2">
              <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate text-xs text-ink">{d.name}</span>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onDownload(d)}><Download className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onDeleteDoc(d.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
            </div>
          </div>
        ))}
        <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-navy hover:underline" data-testid={`session-upload-${s.id}`}>
          <Upload className="h-3.5 w-3.5" /> Attach file (report / prescription / photo)
          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
            onChange={(e) => { onUpload(e.target.files[0]); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
}

function LogSessionDialog({ treatment, doctors, onClose, onDone }) {
  const empty = { session_date: "", notes: "", prescription: "", observations: "", doctor_id: "", next_visit: "", consumes_session: true };
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]); // File[] queued for this session
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (treatment) { setForm(empty); setFiles([]); } /* eslint-disable-next-line */ }, [treatment]);
  if (!treatment) return null;

  const addFiles = (list) => setFiles((prev) => [...prev, ...Array.from(list || [])]);
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setSaving(true);
    try {
      // 1) Create the session, 2) upload any queued files attached to it.
      const { data: session } = await api.post(`/treatments/${treatment.id}/sessions`, {
        session_date: form.session_date ? new Date(form.session_date).toISOString() : null,
        notes: form.notes, prescription: form.prescription, observations: form.observations,
        doctor_id: form.doctor_id || null,
        next_visit: form.next_visit ? new Date(form.next_visit).toISOString() : null,
        consumes_session: form.consumes_session,
      });
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("doc_type", "session");
        fd.append("patient_id", treatment.patient_id);
        fd.append("treatment_id", treatment.id);
        fd.append("session_id", session.id);
        await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length ? `Session logged · ${files.length} file(s) attached` : "Session logged");
      onClose(); onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!treatment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Log Session — {treatment.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {/* A free review or touch-up records the visit without burning a paid
              session — the most common way a package ledger silently goes wrong. */}
          <div className="flex items-center justify-between rounded-lg bg-cream/60 px-3 py-2.5">
            <div>
              <p className="text-sm font-medium text-ink">Uses a package session</p>
              <p className="text-xs text-muted-foreground">
                {form.consumes_session
                  ? `${treatment.sessions_done}/${treatment.sessions_total} used — this makes it ${Math.min((treatment.sessions_done || 0) + 1, treatment.sessions_total)}`
                  : "Free review / touch-up — no session deducted"}
              </p>
            </div>
            <Switch
              checked={form.consumes_session}
              onCheckedChange={(v) => set("consumes_session", v)}
              data-testid="session-consumes"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Visit Date"><Input type="date" value={form.session_date} onChange={(e) => set("session_date", e.target.value)} data-testid="session-date" /></Field>
            <Field label="Doctor">
              <Select value={form.doctor_id} onValueChange={(v) => set("doctor_id", v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Notes (what was done)"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="session-notes" /></Field>
          <Field label="Prescription"><Textarea value={form.prescription} onChange={(e) => set("prescription", e.target.value)} data-testid="session-prescription" /></Field>
          <Field label="Observations / Vitals"><Textarea value={form.observations} onChange={(e) => set("observations", e.target.value)} /></Field>
          <Field label="Next Visit (advised)"><Input type="date" value={form.next_visit} onChange={(e) => set("next_visit", e.target.value)} /></Field>

          {/* Attachments */}
          <Field label="Attachments (reports / prescription / photos)">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-navy/20 bg-cream/40 py-4 text-sm font-medium text-navy hover:bg-cream/60" data-testid="session-files-input">
              <Upload className="h-4 w-4" /> Choose files
              <input type="file" multiple className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </label>
            {files.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md bg-white px-2.5 py-1.5">
                    <div className="flex min-w-0 items-center gap-2">
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate text-xs text-ink">{f.name}</span>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeFile(i)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-session-btn">
            {saving ? "Saving…" : "Log Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageTreatment({ treatment, onClose, onSave }) {
  const [done, setDone] = useState(0);
  const [status, setStatus] = useState("started");
  const [amount, setAmount] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  useEffect(() => {
    if (treatment) {
      setDone(treatment.sessions_done || 0);
      setStatus(treatment.status || "started");
      setAmount(treatment.amount != null ? String(treatment.amount) : "");
      setExpiresAt(treatment.expires_at ? treatment.expires_at.slice(0, 10) : "");
    }
  }, [treatment]);

  if (!treatment) return null;
  const total = treatment.sessions_total || 1;
  const isPackage = !!treatment.package_id || treatment.package_price != null;

  const save = async () => {
    const body = { sessions_done: parseInt(done, 10) || 0, status };
    // The agreed price is where a package discount lives (invoice-level
    // discounts are rejected for package invoices), so it stays editable.
    if (amount !== "" && parseFloat(amount) !== treatment.amount) body.amount = parseFloat(amount);
    if (expiresAt && expiresAt !== (treatment.expires_at || "").slice(0, 10)) body.expires_at = expiresAt;
    await onSave(treatment.id, body);
    onClose();
  };

  return (
    <Dialog open={!!treatment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-navy">Manage Treatment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-cream/60 px-3 py-2">
            <p className="font-semibold text-ink">{treatment.name}</p>
            {(treatment.package_name || treatment.package) && (
              <p className="text-xs text-muted-foreground">{treatment.package_name || treatment.package}</p>
            )}
          </div>
          <Field label={`Sessions done (of ${total})`}>
            <Input type="number" min={0} max={total} value={done}
              onChange={(e) => setDone(e.target.value)} data-testid="manage-sessions-done" />
          </Field>
          {isPackage && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Agreed price (₹)">
                <Input type="number" inputMode="decimal" value={amount}
                  onChange={(e) => setAmount(e.target.value)} data-testid="manage-amount" />
                {treatment.package_price != null && (
                  <p className="mt-1 text-xs text-muted-foreground">Catalogue {formatCurrency(treatment.package_price)}</p>
                )}
              </Field>
              <Field label="Expires on">
                <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="manage-expires" />
              </Field>
            </div>
          )}
          <Field label="Status">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="manage-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TREATMENT_STATUS).map(([v, s]) => <SelectItem key={v} value={v}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-manage-treatment-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddTreatment({ open, onOpenChange, patientId, onDone }) {
  const empty = { name: "", package: "", package_id: "", sessions_total: 1, sessions_done: 0, amount: 0, notes: "" };
  const [form, setForm] = useState(empty);
  const [packages, setPackages] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(empty);
    api.get("/packages", { params: { active_only: true } })
      .then((r) => setPackages(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = packages.find((p) => p.id === form.package_id);

  // Picking a catalogue package fills the sale in; every field stays editable so
  // a discount or a custom session count is just a normal edit afterwards.
  const pickPackage = (pkgId) => {
    if (pkgId === "__none__") { setForm((f) => ({ ...f, package_id: "" })); return; }
    const pkg = packages.find((p) => p.id === pkgId);
    if (!pkg) return;
    setForm((f) => ({
      ...f,
      package_id: pkg.id,
      name: pkg.name,
      package: "",                        // catalogue package: legacy free text unused
      sessions_total: pkg.sessions,
      amount: pkg.gross_price,
    }));
  };

  const submit = async () => {
    if (!form.name) { toast.error("Treatment name required"); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/treatments`, {
        ...form,
        package_id: form.package_id || null,
        sessions_total: parseInt(form.sessions_total) || 1,
        sessions_done: parseInt(form.sessions_done) || 0,
        amount: parseFloat(form.amount) || 0,
      });
      toast.success("Treatment added");
      setForm(empty); onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const discount = selected ? Math.round((selected.gross_price - (parseFloat(form.amount) || 0)) * 100) / 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-navy">Add Treatment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Package">
            <Select value={form.package_id || "__none__"} onValueChange={pickPackage}>
              <SelectTrigger data-testid="treatment-package-select">
                <SelectValue placeholder="Choose from catalogue…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Custom / one-off (no package)</SelectItem>
                {packages.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} — {formatCurrency(p.gross_price)} · {p.sessions} sessions
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && (
              <p className="mt-1 text-xs text-muted-foreground">
                Catalogue {formatCurrency(selected.gross_price)} · {selected.sessions} sessions
                {selected.validity_days ? ` · valid ${selected.validity_days} days` : " · no expiry"}
                {discount > 0 && <span className="font-medium text-green-700"> · discount {formatCurrency(discount)}</span>}
              </p>
            )}
          </Field>
          <Field label="Treatment Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="treatment-name" /></Field>
          {!form.package_id && (
            <Field label="Package (custom)">
              <Input value={form.package} onChange={(e) => set("package", e.target.value)} placeholder="Free-text label" />
            </Field>
          )}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Total Sessions"><Input type="number" value={form.sessions_total} onChange={(e) => set("sessions_total", e.target.value)} /></Field>
            <Field label="Done"><Input type="number" value={form.sessions_done} onChange={(e) => set("sessions_done", e.target.value)} /></Field>
            <Field label="Agreed (₹)"><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="treatment-amount" /></Field>
          </div>
          <Field label="Notes"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-treatment-btn">
            {saving ? "Saving…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Self-fetching panel, same shape as PatientInvoices below: takes only the
// patient id and owns its own data.
function PackageLedger({ patientId, onChanged }) {
  const [data, setData] = useState(null);
  const [collecting, setCollecting] = useState(null);   // treatment we're taking money for
  const [viewing, setViewing] = useState(null);         // treatment whose detail is open

  const load = useCallback(() => {
    api.get(`/patients/${patientId}/packages`)
      .then((r) => setData(r.data)).catch(() => setData({ items: [], totals: {} }));
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const withMoney = data.items.filter((i) => i.ledger);
  if (withMoney.length === 0) return null;   // nothing priced yet — don't show an empty shell

  const t = data.totals || {};
  const refresh = () => { load(); onChanged?.(); };

  return (
    <div className="rounded-xl border border-navy/10 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <PackageIcon className="h-5 w-5 text-navy" />
        <h3 className="font-display text-lg font-semibold text-navy">Packages &amp; Balance</h3>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Agreed", value: t.agreed, tone: "text-navy" },
          { label: "Collected", value: t.paid, tone: "text-green-700" },
          { label: "Outstanding", value: t.due, tone: t.due > 0 ? "text-red-600" : "text-muted-foreground" },
          { label: "Not invoiced", value: t.unbilled, tone: t.unbilled > 0 ? "text-amber-700" : "text-muted-foreground" },
        ].map((tile) => (
          <div key={tile.label} className="rounded-lg bg-cream/60 px-3 py-2.5 text-center">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{tile.label}</p>
            <p className={`font-display text-lg font-semibold ${tile.tone}`}>{formatCurrency(tile.value || 0)}</p>
          </div>
        ))}
      </div>

      {t.expiring_30d > 0 && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {t.expiring_30d} package{t.expiring_30d !== 1 ? "s" : ""} expiring within 30 days.
        </p>
      )}

      <div className="mt-4 space-y-2">
        {withMoney.map((row) => {
          const m = row.ledger;
          const exp = expiryChip(row.expires_at);
          return (
            <div key={row.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-navy/10 px-3 py-2.5" data-testid={`ledger-row-${row.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-medium text-ink">{row.package_name || row.name}</p>
                  {exp && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${exp.tone}`}>{exp.label}</span>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {row.sessions_done}/{row.sessions_total} sessions · {row.sessions_left} left
                  {m.discount > 0 && ` · ${formatCurrency(m.discount)} discount`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-ink">{formatCurrency(m.amount_paid)} <span className="text-muted-foreground">/ {formatCurrency(m.agreed_price)}</span></p>
                {m.balance_due > 0
                  ? <p className="text-xs font-medium text-red-600">{formatCurrency(m.balance_due)} due</p>
                  : <p className="text-xs font-medium text-green-700">Paid in full</p>}
              </div>
              <div className="flex items-center gap-1.5">
                {m.balance_due > 0 && (
                  <Button size="sm" onClick={() => setCollecting(row)} className="h-8 bg-navy text-white hover:bg-navy-hover" data-testid={`collect-${row.id}`}>
                    Collect
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setViewing(row)} className="h-8 border-navy/20 text-navy" data-testid={`view-ledger-${row.id}`}>
                  Ledger
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <CollectPaymentDialog treatment={collecting} onClose={() => setCollecting(null)} onDone={refresh} />
      <LedgerDetailDialog treatment={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

// Raises an installment invoice for the amount taken, then records the payment
// against it — so one action at the desk keeps the invoice trail and the
// package balance in step.
function CollectPaymentDialog({ treatment, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (treatment) setAmount(String(treatment.ledger?.balance_due ?? ""));
  }, [treatment]);

  if (!treatment) return null;
  const m = treatment.ledger || {};
  const value = parseFloat(amount) || 0;

  const submit = async () => {
    if (value <= 0) { toast.error("Enter an amount"); return; }
    setSaving(true);
    try {
      let invoiceId;
      // Reuse an already-raised unpaid invoice when one covers this payment;
      // only raise a new installment when the money isn't billed yet.
      const { data: existing } = await api.get("/invoices", {
        params: { treatment_id: treatment.id, patient_id: treatment.patient_id },
      });
      const open = (existing.items || []).find(
        (i) => i.status !== "cancelled" && i.status !== "paid" && (i.amount_due || 0) >= value);
      if (open) {
        invoiceId = open.id;
      } else {
        const { data: inv } = await api.post(`/treatments/${treatment.id}/invoice`, { amount: value });
        invoiceId = inv.id;
      }
      await api.post("/payments", { invoice_id: invoiceId, amount: value, method });
      toast.success(`${formatCurrency(value)} collected`);
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!treatment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-navy">Collect Payment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-cream/60 px-3 py-2.5">
            <p className="font-semibold text-ink">{treatment.package_name || treatment.name}</p>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(m.amount_paid)} paid of {formatCurrency(m.agreed_price)} · {formatCurrency(m.balance_due)} due
            </p>
          </div>
          <Field label="Amount (₹)">
            <Input type="number" inputMode="decimal" value={amount} autoFocus
              onChange={(e) => setAmount(e.target.value)} data-testid="collect-amount" />
          </Field>
          <Field label="Method">
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="collect-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["cash", "card", "upi", "bank_transfer", "cheque", "other"].map((mth) => (
                  <SelectItem key={mth} value={mth}>{mth.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {value > (m.balance_due || 0) && (
            <p className="text-xs text-amber-700">That is more than the {formatCurrency(m.balance_due)} outstanding.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="collect-confirm-btn">
            {saving ? "Saving…" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LedgerDetailDialog({ treatment, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!treatment) { setData(null); return; }
    api.get(`/treatments/${treatment.id}/ledger`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [treatment]);

  if (!treatment) return null;
  return (
    <Dialog open={!!treatment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">{treatment.package_name || treatment.name}</DialogTitle>
        </DialogHeader>
        {!data ? <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Agreed", data.ledger.agreed_price], ["Paid", data.ledger.amount_paid], ["Due", data.ledger.balance_due]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-cream/60 px-2 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
                  <p className="font-display text-base font-semibold text-navy">{formatCurrency(v)}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Invoices</p>
              {data.invoices.length === 0 ? <p className="text-sm text-muted-foreground">None raised yet.</p> : (
                <div className="space-y-1">
                  {data.invoices.map((i) => (
                    <div key={i.id} className="flex items-center justify-between rounded border border-navy/10 px-2.5 py-1.5 text-sm">
                      <span className="font-mono text-xs text-muted-foreground">{i.invoice_number}</span>
                      <span className="text-muted-foreground">{formatDate(i.created_at)}</span>
                      <span>{formatCurrency(i.amount_paid)} / {formatCurrency(i.grand_total)}</span>
                      <span className="text-xs capitalize text-muted-foreground">{i.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payments</p>
              {data.payments.length === 0 ? <p className="text-sm text-muted-foreground">Nothing collected yet.</p> : (
                <div className="space-y-1">
                  {data.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded border border-navy/10 px-2.5 py-1.5 text-sm">
                      <span className="font-medium text-ink">{formatCurrency(p.amount)}</span>
                      <span className="text-xs capitalize text-muted-foreground">{(p.method || "").replace("_", " ")}</span>
                      <span className="text-xs text-muted-foreground">{p.received_by_name}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(p.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PatientInvoices({ patientId }) {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    api.get("/invoices", { params: { patient_id: patientId, limit: 5 } })
      .then((r) => setInvoices(r.data.items || []))
      .catch(() => {});
  }, [patientId]);

  if (!invoices.length) return <p className="mt-3 text-sm text-muted-foreground">No invoices yet.</p>;

  return (
    <div className="mt-3 space-y-2">
      {invoices.map((inv) => (
        <div key={inv.id} className="flex items-center justify-between rounded-lg bg-cream/60 px-3 py-2">
          <div>
            <p className="text-sm font-medium text-ink">{inv.invoice_number}</p>
            <p className="text-xs text-muted-foreground">{formatDateTime(inv.created_at)}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-navy">{formatCurrency(inv.grand_total)}</p>
            <span className={`text-[10px] font-semibold capitalize ${inv.status === "paid" ? "text-green-600" : inv.status === "partial" ? "text-amber-600" : "text-muted-foreground"}`}>{inv.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
